import { useState } from 'react';
import { Key, Mail, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../../store/useAuthStore';
import { supabase, isAuthConfigured } from '../../../lib/supabase';
import { profileApi } from '../../../api/client';

// ── Password strength ─────────────────────────────────────────────────────────

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score, label: 'Weak', color: 'bg-loss' };
  if (score <= 2) return { score, label: 'Fair', color: 'bg-warn' };
  if (score <= 3) return { score, label: 'Good', color: 'bg-brand' };
  return { score, label: 'Strong', color: 'bg-win' };
}

// ── Password input ────────────────────────────────────────────────────────────

function PasswordInput({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 pr-10 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SecuritySettingsPage() {
  const { user, updatePassword } = useAuthStore();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwStatus, setPwStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [pwError, setPwError] = useState('');

  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'success' | 'pending' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  const strength = getPasswordStrength(newPassword);

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setPwError('');
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters');
      return;
    }

    setPwSaving(true);
    setPwStatus('idle');
    try {
      // Reauthenticate with current password first
      if (isAuthConfigured && user?.email) {
        const { error: reAuthError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });
        if (reAuthError) {
          setPwStatus('error');
          setPwError('Current password is incorrect');
          return;
        }
      }

      await updatePassword(newPassword);

      setPwStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPwStatus('idle'), 4000);
    } catch (err: unknown) {
      setPwStatus('error');
      setPwError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setPwSaving(false);
    }
  }

  async function handleEmailChange(e: React.FormEvent) {
    e.preventDefault();
    setEmailError('');
    if (!newEmail.trim()) {
      setEmailError('Email address is required');
      return;
    }

    setEmailSaving(true);
    setEmailStatus('idle');
    try {
      // Reauthenticate first
      if (isAuthConfigured && user?.email) {
        const { error: reAuthError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: emailPassword,
        });
        if (reAuthError) {
          setEmailStatus('error');
          setEmailError('Password is incorrect');
          return;
        }
      }

      // Request email change
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;

      setEmailStatus('pending');
      setNewEmail('');
      setEmailPassword('');
    } catch (err: unknown) {
      setEmailStatus('error');
      setEmailError(err instanceof Error ? err.message : 'Failed to request email change');
    } finally {
      setEmailSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Security</h2>
        <p className="text-sm text-gray-500 mt-1">Manage your password and email address.</p>
      </div>

      {/* Change Password */}
      <form onSubmit={handlePasswordChange} className="op-panel p-4 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Key className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-300">Change Password</h3>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Current Password</label>
          <PasswordInput
            id="current-password"
            value={currentPassword}
            onChange={setCurrentPassword}
            placeholder="Enter current password"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">New Password</label>
          <PasswordInput
            id="new-password"
            value={newPassword}
            onChange={setNewPassword}
            placeholder="At least 8 characters"
          />
          {newPassword && (
            <div className="mt-2 space-y-1">
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={clsx(
                      'h-1 flex-1 rounded-full transition-colors',
                      strength.score >= i ? strength.color : 'bg-surface-4',
                    )}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500">{strength.label}</p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Confirm New Password</label>
          <PasswordInput
            id="confirm-password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Re-enter new password"
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-loss mt-1">Passwords do not match</p>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={pwSaving || !currentPassword || !newPassword || !confirmPassword}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {pwSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            Update Password
          </button>
          {pwStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-sm text-win">
              <CheckCircle className="w-4 h-4" /> Password updated
            </span>
          )}
          {pwStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-sm text-loss">
              <AlertCircle className="w-4 h-4" /> {pwError}
            </span>
          )}
        </div>
      </form>

      {/* Change Email */}
      <form onSubmit={handleEmailChange} className="op-panel p-4 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-300">Change Email Address</h3>
        </div>

        <div className="px-3 py-2 bg-surface-3 border border-surface-4 rounded-md">
          <p className="text-xs text-gray-500">Current email</p>
          <p className="text-sm text-gray-300 mt-0.5">{user?.email ?? '—'}</p>
        </div>

        {emailStatus === 'pending' && (
          <div className="flex items-start gap-2 px-3 py-3 bg-brand/10 border border-brand/30 rounded-md">
            <CheckCircle className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-brand font-medium">Verification email sent</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Check your new email address to confirm the change. The change will not take effect until you click the link.
              </p>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">New Email Address</label>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="new@example.com"
            className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">
            Confirm with Password
          </label>
          <PasswordInput
            value={emailPassword}
            onChange={setEmailPassword}
            placeholder="Your current password"
          />
          <p className="text-xs text-gray-600 mt-1">
            Required to verify your identity before changing email.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={emailSaving || !newEmail || !emailPassword}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {emailSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            Request Email Change
          </button>
          {emailStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-sm text-loss">
              <AlertCircle className="w-4 h-4" /> {emailError}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
